import {
  Component, Input, OnDestroy, OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import {
  distinctUntilChanged, filter, map, takeUntil,
} from 'rxjs/operators';
import { ProjectsService } from '../../../../../core/projects.service';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { AppService, AuthService } from '../../../../../../../shared/services';
import { WorkflowService } from '../../../../../../../shared/services/workflow.service';
import { FetchLineageRequest } from '../../../../../core/projects.actions';
import { lineageList, projectDetails } from '../../../../../core/projects.selectors';
import { projectForRoute } from '../../../../../../../shared/helpers/project-widget-config';
import {
  PunchlistItemField,
  resolvePunchlistItemFields,
} from '../../../../../../../shared/helpers/project-punchlist-item-config';
import {
  punchlistItemValuesText,
  PunchlistMultiValueField,
} from '../../../../../../../shared/helpers/punchlist-item-values';
import { issueStatusText } from '../../../../../../../shared/helpers/issue-workflow';

// Punch list items follow the issue workflow: Draft, Submitted, Forwarded, Responded, Closed.
// verified / sent_back / rejected are legacy values kept only so older rows still get a chip.
const STATUS_MAP: Record<string, { label: string; bgColor: string; textColor: string }> = {
  draft: { label: 'Draft', bgColor: 'bg-draft', textColor: 'txt-draft' },
  submitted: { label: 'Submitted', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
  sent_to_contractor: { label: 'Forwarded', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
  responded: { label: 'Responded', bgColor: 'bg-submitted', textColor: 'txt-submitted' },
  closed: { label: 'Closed', bgColor: 'bg-approved', textColor: 'txt-approved' },
  verified: { label: 'Verified', bgColor: 'bg-approved', textColor: 'txt-approved' },
  sent_back: { label: 'Sent back', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
  rejected: { label: 'Rejected', bgColor: 'bg-rejected', textColor: 'txt-rejected' },
};

/** A next-state formInput (punch list states use text, text[] and file[]). */
export interface PunchlistActionInput {
  name: string;
  type: string;
  label?: string;
  required?: boolean;
}

// Chip colours for the system default severities. A project or organization
// can have its own severity list; a value without an entry here is shown as
// plain text.
const SEVERITY_MAP: Record<string, { label: string; bgColor: string; textColor: string }> = {
  Low: { label: 'Low', bgColor: 'bg-low', textColor: 'txt-low' },
  Medium: { label: 'Medium', bgColor: 'bg-medium', textColor: 'txt-medium' },
  High: { label: 'High', bgColor: 'bg-high', textColor: 'txt-high' },
  Critical: { label: 'Critical', bgColor: 'bg-critical', textColor: 'txt-critical' },
};

@Component({
  selector: 'app-punchlist-item-detail',
  templateUrl: './punchlist-item-detail.component.html',
  styleUrls: ['./punchlist-item-detail.component.scss'],
  standalone: false,
})
export class PunchlistItemDetailComponent implements OnInit, OnDestroy {
  /** A linked issue's status as its badge prints it (a contractor-workflow stage in words). */
  readonly issueStatusText = issueStatusText;

  @Input() data: any;
  @Input() readOnly = false;
  item: any = null;
  issues: any[] = [];
  subAccounts: any[] = [];
  role = '';
  loading = false;
  acting = false;
  selectedTabIndex = 0;
  statusMap = STATUS_MAP;
  severityMap = SEVERITY_MAP;

  /**
   * The item fields this project shows, in order: its punchlist_item_config
   * when it has one, the built-in fields otherwise (same list as the item
   * form). Null until the project is in the store; the rows it governs are
   * not rendered before that, so a field the project hides never shows.
   */
  fields: PunchlistItemField[] | null = null;

  actionForm: FormGroup;
  /** Actions from GET /v1/workflow/next-state; value = workflow state name. */
  actionOptions: { value: string; label: string }[] = [];
  /** formInputs per state, from next-state options. */
  actionInputs: Record<string, PunchlistActionInput[]> = {};
  /** formInputs of the currently picked action. */
  selectedInputs: PunchlistActionInput[] = [];
  actionMediaFiles: any[] = [];
  workflowHistory: any[] = [];
  forwardCandidateOptions: { value: any; label: string }[] = [];

  private readonly onDestroy = new Subject<any>();

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private authService: AuthService,
    public appService: AppService,
    private workflowService: WorkflowService,
  ) {
    this.actionForm = this.fb.group({ action: [null] });
  }

  ngOnInit(): void {
    const account = this.authService.getCurrentUser();
    this.role = account?.roles?.find((r: any) => r.project_id === this.data?.projectId)?.role || '';
    this.item = { ...this.data };
    this.refresh();
    this.fetchLineage();

    // The store keeps whichever project was fetched last and nothing while a
    // fetch is in flight: only the item's own project counts, and the fields
    // it gave stand through a refetch.
    this.store.select(projectDetails)
      .pipe(
        takeUntil(this.onDestroy),
        map(project => projectForRoute(project, this.data?.projectId)),
        filter(project => !!project),
        map(project => resolvePunchlistItemFields(project)),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      )
      .subscribe((fields: PunchlistItemField[]) => { this.fields = fields; });

    this.actionForm.get('action')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((state: string | null) => this.onActionPicked(state));

    // Listen for uploaded files tagged for action attachments
    this.commonService.uploadedFilesInfo
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        const items = JSON.parse(JSON.stringify(Array.isArray(data) ? data : [data]));
        const newFiles = items
          .filter((i: any) => i?.fileType === 'picture' || i?.fileType === 'document')
          .map((f: any) => ({ ...f, tags: ['actions-attachments'] }));
        if (newFiles.length) {
          this.actionMediaFiles = [...this.actionMediaFiles, ...newFiles];
          setTimeout(() => this.projectsService.mediaData.next(this.actionMediaFiles));
        }
      });

    this.store.select(lineageList)
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        if (data?.length) {
          const cloned = JSON.parse(JSON.stringify(data));
          // Only show lineage entries for this item's punchlist-item workflow
          const itemId = this.item?.uuid || this.item?.id;
          const relevant = cloned.filter((step: any) => {
            const ex = step?.execution_id || step?.ex || '';
            return !ex || ex === `punchlist-item/${itemId}`;
          });
          this.workflowHistory = relevant.map((step: any) => {
            const stateName = step?.state?.name || '';
            let forwardedTo = step?.input?.formValues?.forwarded_to || [];
            // Fallback for legacy forward entries that didn't capture recipient names —
            // show all current forwarded recipients on the item.
            if (stateName === 'punchlist-forward-sub' && !forwardedTo.length && this.subAccounts?.length) {
              forwardedTo = this.subAccounts.map(
                (s: any) => `${s.first_name || ''} ${s.last_name || ''}`.trim(),
              ).filter(Boolean);
            }
            return {
              action: step?.state?.comment || stateName || 'Action',
              state_name: stateName,
              actor_name: step?.actor ? `${step.actor.first_name} ${step.actor.last_name}` : 'Unknown',
              actor_initials: this.getInitials(step?.actor),
              date: step?.created_at,
              notes: step?.input?.formValues?.comments || step?.input?.description || null,
              attachments: step?.attachments || [],
              forwarded_to: forwardedTo,
            };
          });
        }
      });
  }

  refresh(): void {
    const itemId = this.item.uuid || this.item.id;
    if (!itemId) return;
    this.loading = true;
    this.projectsService.fetchPunchlistItem({ itemId })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          this.loading = false;
          const fetched = resp?.data
            ? { ...resp.data, ...(resp.data.attributes || {}) }
            : null;
          if (fetched) {
            this.item = { ...this.item, ...fetched };
            this.issues = fetched.issues || [];
            this.subAccounts = fetched.sub_accounts || [];
          }
          this.fetchActionOptions();
        },
        error: () => { this.loading = false; },
      });
  }

  /** Candidates for a text[] account_ids input ("Forward to"); the API scopes the list per caller. */
  fetchForwardCandidates(): void {
    const itemId = this.item?.uuid || this.item?.id;
    if (!itemId) return;
    this.projectsService.fetchPunchlistForwardCandidates({ itemId })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (resp: any) => {
          const rows: any[] = Array.isArray(resp) ? resp : (resp?.data || []);
          this.forwardCandidateOptions = rows.map((u: any) => {
            const name = (u.name || `${u.first_name || ''} ${u.last_name || ''}`).trim() || u.email;
            const company = u.sub_contractor?.name ? ` - ${u.sub_contractor.name}` : '';
            return {
              value: u.uuid || u.id,
              label: `${name}${company}` + (u.email && u.email !== name ? ` (${u.email})` : ''),
            };
          });
        },
        error: () => { this.forwardCandidateOptions = []; },
      });
  }

  fetchLineage(): void {
    const itemId = this.item?.uuid || this.item?.id;
    if (!itemId) return;
    this.store.dispatch(FetchLineageRequest({
      payload: {
        qp: {
          start: 0,
          total: 1000,
          ex: `punchlist-item/${itemId}`,
          include: ['actor', 'state', 'attachments'],
        },
      },
    }));
  }

  /** The item's categories or types (it can hold several), joined with ", ". */
  valuesText(field: PunchlistMultiValueField): string {
    return punchlistItemValuesText(this.item, field);
  }

  /** The project shows this field (its config, or the built-in list). */
  showsField(name: PunchlistItemField['name']): boolean {
    return !!this.fields?.some((field) => field.name === name);
  }

  getInitials(actor: any): string {
    if (!actor) return '?';
    return `${(actor.first_name || '')[0] || ''}${(actor.last_name || '')[0] || ''}`.toUpperCase() || '?';
  }

  /**
   * Actions come from the workflow engine's next-state (roles, statuses and labels are
   * server-owned), so the dropdown matches what the API will accept for this user.
   */
  fetchActionOptions(): void {
    const itemId = this.item?.uuid || this.item?.id;
    if (!itemId || this.readOnly || !this.workflowService.canExecute()) {
      this.setActions([]);
      return;
    }
    this.workflowService.getNextActions('punchlist-item', itemId, this.data?.projectId)
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: (actions: any[]) => this.setActions(actions || []),
        error: () => this.setActions([]),
      });
  }

  setActions(actions: any[]): void {
    this.actionInputs = {};
    this.actionOptions = actions
      .filter((a: any) => a?.state)
      .map((a: any) => {
        this.actionInputs[a.state] = Array.isArray(a.options?.formInputs) ? a.options.formInputs : [];
        return { value: a.state, label: a.options?.label || a.state };
      });
    const picked = this.actionForm.get('action')?.value;
    if (picked && !this.actionInputs[picked]) this.actionForm.get('action')?.setValue(null);
    if (Object.values(this.actionInputs).some((inputs) => inputs.some((i) => i.type === 'text[]'))) {
      this.fetchForwardCandidates();
    }
  }

  /** Rebuilds the action form's input controls for the picked state's formInputs. */
  onActionPicked(state: string | null): void {
    Object.keys(this.actionForm.controls)
      .filter((name) => name !== 'action')
      .forEach((name) => this.actionForm.removeControl(name, { emitEvent: false }));
    this.selectedInputs = (state && this.actionInputs[state]) || [];
    this.selectedInputs
      .filter((input) => input.type === 'text' || input.type === 'text[]')
      .forEach((input) => this.actionForm.addControl(
        input.name, new FormControl(input.type === 'text[]' ? [] : ''), { emitEvent: false },
      ));
  }

  hasFileInput(): boolean {
    return this.selectedInputs.some((input) => input.type === 'file[]');
  }

  deleteActionPicture(pic: any): void {
    this.actionMediaFiles = this.actionMediaFiles.filter((p: any) => p.id !== pic.id);
    setTimeout(() => this.projectsService.mediaData.next(this.actionMediaFiles));
  }

  executeAction(): void {
    const state = this.actionForm.get('action')?.value;
    if (!state || this.acting) return;

    const formValues: Record<string, any> = {};
    for (const input of this.selectedInputs) {
      if (input.type === 'file[]') {
        formValues[input.name] = this.actionMediaFiles.map((f: any) => f.id);
        continue;
      }
      const value = this.actionForm.get(input.name)?.value;
      if (input.type === 'text[]') {
        const ids: any[] = Array.isArray(value) ? value : [];
        if (input.required && !ids.length) {
          this.commonService.errorNotification(`Pick at least one user for ${input.label || input.name}`);
          return;
        }
        formValues[input.name] = ids;
        if (input.name === 'account_ids') {
          // Display-only: lets the Discussion tab show who the item was forwarded to.
          formValues.forwarded_to = ids
            .map((id: any) => this.forwardCandidateOptions.find((o) => o.value === id)?.label)
            .filter(Boolean);
        }
      } else if (input.type === 'text') {
        const text = (value || '').trim();
        if (input.required && !text) {
          this.commonService.errorNotification(`${input.label || input.name} is required`);
          return;
        }
        formValues[input.name] = text;
      }
    }

    this.acting = true;
    this.projectsService.executePunchlistItemState({
      itemId: this.item.uuid || this.item.id,
      state,
      formValues,
    }).pipe(takeUntil(this.onDestroy)).subscribe({
      next: () => this.afterAction(),
      error: (err: any) => this.handleError(err),
    });
  }

  afterAction(): void {
    this.acting = false;
    this.actionForm.get('action')?.setValue(null);
    this.actionMediaFiles = [];
    this.commonService.notification('Action completed', 'success');
    this.projectsService.punchlistChanged.next();
    this.refresh();
    setTimeout(() => this.fetchLineage(), 1000);
  }

  handleError(err: any): void {
    this.acting = false;
    this.commonService.errorNotification(err?.error?.error || 'Action failed');
  }

  close(): void { this.projectsService.closeRightPanel.next(); }

  ngOnDestroy(): void {
    this.onDestroy.next(null); this.onDestroy.complete();
  }
}
